import { yupResolver } from "@hookform/resolvers/yup";
import dayjs, { Dayjs } from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useReopenPurchaseOrdersMutation } from "../../../../api/services/client/clientApis";
import { GetPurchaseOrderResponse } from "../../../../api/services/client/models";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTypography,
} from "../../../../components";
import { MuiDateFieldFormController } from "../../../../components/dateField/MuiDateFieldFormController";
import { ONE_DAY_IN_MS, PAGE_IDS } from "../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { ReopenPurchaseOrderFormKey } from "../../modelsAndSchemas/Models";
import { purchaseOrdersListSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ConfirmatoryPopupWrapper } from "../addNewContact/AddNewContact.style";
import { ReOpenPurchaseOrderWrapper } from "./ReOpenPurchaseOrder.style";
import { ReOpenPurchaseOrderValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { useToast } from "../../../../hooks/useToast";
import { useGridContainer } from "../../../../hooks/useGridContainer";

interface ReOpenPurchaseOrderProps {
  open: boolean;
  onClose: () => void;
  onPurchaseOrderAdded?: (purchaseOrderId?: string) => void;
  purchaseOrderData?: GetPurchaseOrderResponse;
}

export const ReOpenPurchaseOrder: FC<ReOpenPurchaseOrderProps> = ({
  open,
  onClose,
  onPurchaseOrderAdded,
  purchaseOrderData,
}) => {
  const pageId = PAGE_IDS.ADD_PURCHASE_ORDER;
  const { gridSizeLarge: gridSize } = useGridContainer();
  const { isMobile } = useMediaQueries();
  const dispatch = useDispatch();
  const { id: clientId } = useSelector(createClientSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const [minExpiryDate, setMinExpiryDate] = useState<Dayjs | null>(null);

  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [showConfirmatoryPopup, setShowConfirmatoryPopup] = useState(false);
  const { displayToast } = useToast();

  const {
    purchaseOrderTitle,
    reOpenDate,
    reOpenReasonDropdown,
    poReOpenReasonList,
    reOpenPurchaseOrderHeader,
  } = purchaseOrdersListSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(purchaseOrderTitle, key);
  };

  const buttonSave = commonLabels.save;
  const buttonCancel = commonLabels.cancel;

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [ReopenPurchaseOrderFormKey.reOpenReason]: "",
      [ReopenPurchaseOrderFormKey.expiryDate]: "",
    },
    resolver: yupResolver(
      ReOpenPurchaseOrderValidationSchema(commonValidationMessages),
    ),
  });

  const {
    formState: { dirtyFields },
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
  } = methods;

  const {
    mutateAsync: reOpenPurchaseOrder,
    isSuccess: isReOpenPurchaseOrderSuccess,
    isPending: isReOpenPurchaseOrderPending,
    isError: isReOpenPurchaseOrderError,
  } = useReopenPurchaseOrdersMutation();

  useEffect(() => {
    if (purchaseOrderData) {
      const expirationDateValue = purchaseOrderData?.expiryDate
        ? dayjs(purchaseOrderData.expiryDate)
            .utc(false)
            .startOf("day")
            .format(userCountryDateFormat) // format as string
        : undefined;

      if (
        expirationDateValue &&
        expirationDateValue > dayjs().format(userCountryDateFormat)
      ) {
        setMinExpiryDate(dayjs(expirationDateValue, userCountryDateFormat));
        reset({
          [ReopenPurchaseOrderFormKey.reOpenReason]:
            purchaseOrderData.reOpenReason,
          [ReopenPurchaseOrderFormKey.expiryDate]: expirationDateValue,
        });
      }
    }
  }, [purchaseOrderData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isReOpenPurchaseOrderPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
  }, [isReOpenPurchaseOrderPending]);

  useEffect(() => {
    if (isReOpenPurchaseOrderSuccess) {
      displayToast(
        getLabel(
          purchaseOrdersListSiteCoreKeys.purchaseOrderReOpenedToastMessage,
        ),
        "success",
      );
      onPurchaseOrderAdded?.();
      onClose();
    }
  }, [isReOpenPurchaseOrderSuccess]);

  useEffect(() => {
    if (isReOpenPurchaseOrderError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isReOpenPurchaseOrderError]);

  const handleConfirmatoryPopup = () => {
    if (isNotEmpty(dirtyFields)) {
      setShowConfirmatoryPopup(true);
    } else {
      onClose();
    }
  };

  const hideConfirmatoryPopup = () => {
    setShowConfirmatoryPopup(false);
  };

  const handleConfirmModal = () => {
    const { reOpenReason, expiryDate } = getValues();

    reOpenPurchaseOrder({
      clientId: clientId ?? "",
      purchaseOrderId: getValueOrBlank(purchaseOrderData?.purchaseOrderId),
      reOpenReason: reOpenReason,
      expiryDate: convertToUTCFormat(expiryDate, userCountryDateFormat, true),
      action: "ReOpen",
    });
  };

  const getDateValue = (key: keyof typeof ReopenPurchaseOrderFormKey) => {
    const value = isNotEmpty(getValues(key))
      ? (getValues(key) as unknown as string)
      : null;
    return dayjs(value, userCountryDateFormat);
  };

  const modalTitle = useMemo(() => {
    return `${replaceHashWithValue(getLabel(reOpenPurchaseOrderHeader), getValueOrBlank(purchaseOrderData?.poNumber))}`;
  }, []);

  const minDate = dayjs(new Date().getTime() + ONE_DAY_IN_MS);

  const handleExpireDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(
      ReopenPurchaseOrderFormKey.expiryDate,
      newValue ? newValue.format(userCountryDateFormat) : "",
    );
  };

  return (
    <MuiModal
      open={open}
      onClose={handleConfirmatoryPopup}
      title={modalTitle}
      primaryButtonTitle={buttonSave}
      secondaryButtonTitle={buttonCancel}
      onSecondaryButtonClick={handleConfirmatoryPopup}
      onPrimaryButtonClick={() => handleSubmit(handleConfirmModal)()}
    >
      <FormProvider {...methods}>
        <ReOpenPurchaseOrderWrapper>
          <MuiBox className="content">
            <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 5}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={ReopenPurchaseOrderFormKey.reOpenReason}
                >
                  <MuiSelect
                    id={`${pageId}-${ReopenPurchaseOrderFormKey.reOpenReason}`}
                    label={getLabel(reOpenReasonDropdown)}
                    options={fetchDropdownValues(
                      purchaseOrderTitle,
                      poReOpenReasonList,
                    )}
                    visibleDefaultSelectOption
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                    isMandatory
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={ReopenPurchaseOrderFormKey.expiryDate}
                  value={getDateValue(ReopenPurchaseOrderFormKey.expiryDate)}
                  disablePast
                  id={`${pageId}-${ReopenPurchaseOrderFormKey.expiryDate}`}
                  minDate={minExpiryDate ?? minDate}
                  label={getLabel(reOpenDate)}
                  className="date-picker"
                  clearable
                  format={userCountryDateFormat}
                  valueFormat={userCountryDateFormat}
                  onChange={handleExpireDateValidation}
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                  isMandatory
                />
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
          {showConfirmatoryPopup && (
            <MuiModal
              id={COMPONENT_IDS.CONFIRMATION_MODAL}
              onClose={hideConfirmatoryPopup}
              title={commonLabels.confirmation}
              open={showConfirmatoryPopup}
              onPrimaryButtonClick={onClose}
              onSecondaryButtonClick={hideConfirmatoryPopup}
              secondaryButtonTitle={commonLabels.no}
              primaryButtonTitle={commonLabels.yes}
            >
              <ConfirmatoryPopupWrapper>
                <MuiTypography
                  id={`${pageId}-confirmation-message`}
                  variant="h3"
                  className="confirmatory-message"
                >
                  {commonLabels.discardChangesGivenInfoWillLostText}
                </MuiTypography>
              </ConfirmatoryPopupWrapper>
            </MuiModal>
          )}
        </ReOpenPurchaseOrderWrapper>
      </FormProvider>
    </MuiModal>
  );
};
